import { useEffect, useId, useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter } from "lucide-react";
import { inDayRange, type DayRange } from "../lib/dayRange";

// --- Column definition ---
interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  // On a serverSide table, the server can order by this column: its header
  // drives onServerSortChange. Columns without it stay unsortable there.
  serverSortable?: boolean;
  initialSortDirection?: "asc" | "desc";
  render?: (row: T) => React.ReactNode;
  width?: string;
}

// --- Filter bar ---
interface FilterConfig {
  searchPlaceholder?: string;
  // `value` must be the actual key on the row for client-side filtering
  // to work. If a column needs a friendlier label, put it in `label`.
  searchFields?: { value: string; label: string }[];
  // Longest search term the server accepts (the input stops there).
  searchMaxLength?: number;
  statusOptions?: { value: string; label: string }[];
  statusLabel?: string; // e.g. "Status", "Action", "Type"
  // Row field to compare against the selected status value. Most tables
  // filter on `status`, but audit uses `action` and others may differ.
  // Defaults to "status".
  statusKey?: string;
  multiSelectFilters?: {
    key: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  checkboxFilters?: {
    key: string;
    label: string;
    mode?: "truthy" | "falsy" | "missing";
  }[];
  // From/to day pickers in the "More filters" panel, one pair each. `key`
  // is the row's ISO timestamp field for client-side tables; server-side
  // tables map it to their own params. A table without any shows no
  // "More filters" button.
  dateRangeFilters?: { key: string; label: string }[];
}

export interface DataTableFilterState {
  search: string;
  searchField: string;
  status: string;
  multiSelect: Record<string, string[]>;
  checkbox: Record<string, boolean>;
  dateRanges: Record<string, DayRange>;
}

export interface ServerSort {
  key: string;
  direction: "asc" | "desc";
}

function nextServerSort(current: ServerSort | null | undefined, key: string, first: "asc" | "desc" = "desc"): ServerSort | null {
  if (current?.key !== key) return { key, direction: first };
  if (current.direction === first) return { key, direction: first === "asc" ? "desc" : "asc" };
  return null;
}

// --- Props ---
interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectable?: boolean;
  // Notified whenever the selection set changes. Consumers that need
  // to drive downstream actions (bulk ops, "create task from selected",
  // etc.) subscribe here. DataTable still manages the internal state.
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  filters?: FilterConfig;
  onFilterChange?: (filters: DataTableFilterState) => void;
  pagination?: {
    page: number;
    // Omit when the total is unknown (cursor paging with no count); the
    // pager then shows "Page N" instead of a made-up "N of N+1".
    totalPages?: number;
    count: number;
    hasMore: boolean;
    onNext: () => void;
    onPrev: () => void;
    onFirst: () => void;
    // e.g. "26–30 of 30". Shown beside the pager when given.
    rangeLabel?: string;
    pageSize?: number;
    pageSizeOptions?: number[];
    onPageSizeChange?: (size: number) => void;
  };
  // The server pages, filters and counts this table. Rows render exactly
  // as given: no in-memory filtering and no column sorting, which over
  // one page would hide matches and mis-order rows on every other page.
  // Filter changes still reach the caller through onFilterChange.
  serverSide?: boolean;
  // The server's current order, and the callback a serverSortable header
  // calls. A header click cycles: newest/largest first, then ascending,
  // then back to the server's default order (null).
  serverSort?: ServerSort | null;
  onServerSortChange?: (sort: ServerSort | null) => void;
  // Replaces "No data found" in an empty table.
  emptyMessage?: React.ReactNode;
  title?: string;
  totalCount?: number;
  actions?: React.ReactNode;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  selectable,
  onSelectionChange,
  filters,
  onFilterChange,
  pagination,
  serverSide = false,
  serverSort,
  onServerSortChange,
  emptyMessage,
  title,
  totalCount,
  actions,
  selectedKeys,
}: DataTableProps<T>) {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const selectedKeysFingerprint = selectedKeys?.join("\u0000") ?? "";

  useEffect(() => {
    if (!selectedKeys) return;
    setSelectedRows(new Set(selectedKeys));
  }, [selectedKeys, selectedKeysFingerprint]);

  // Emit selection changes as a sorted array so consumers get a stable
  // identity across renders (sets don't compare by reference).
  function emit(next: Set<string>) {
    setSelectedRows(next);
    onSelectionChange?.(Array.from(next).sort());
  }
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState(filters?.searchFields?.[0]?.value ?? "");
  const [statusFilter, setStatusFilter] = useState("");
  const [multiSelectFilters, setMultiSelectFilters] = useState<Record<string, string[]>>({});
  const [openMultiSelectFilter, setOpenMultiSelectFilter] = useState<string | null>(null);
  const [checkboxFilters, setCheckboxFilters] = useState<Record<string, boolean>>({});
  const [dateRanges, setDateRanges] = useState<Record<string, DayRange>>({});
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const dateInputId = useId();
  const dateRangeFilters = filters?.dateRangeFilters ?? [];

  const allSelected = data.length > 0 && data.every((r) => selectedRows.has(keyExtractor(r)));

  function toggleAll() {
    if (allSelected) {
      emit(new Set());
    } else {
      emit(new Set(data.map(keyExtractor)));
    }
  }

  function toggleRow(key: string) {
    const next = new Set(selectedRows);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    emit(next);
  }

  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  // Emits the filter state with `overrides` applied. Callers pass the
  // value that just changed, because state set in this handler isn't
  // visible until the next render.
  function emitFilters(overrides: { search?: string; searchField?: string } = {}) {
    onFilterChange?.({
      search: overrides.search ?? search,
      searchField: overrides.searchField ?? searchField,
      status: statusFilter,
      multiSelect: multiSelectFilters,
      checkbox: checkboxFilters,
      dateRanges,
    });
  }

  function setDateRangeEnd(key: string, end: keyof DayRange, value: string) {
    const range = { ...dateRanges[key], [end]: value || undefined };
    const next = { ...dateRanges, [key]: range };
    if (!range.from && !range.to) delete next[key];
    setDateRanges(next);
    onFilterChange?.({ search, searchField, status: statusFilter, multiSelect: multiSelectFilters, checkbox: checkboxFilters, dateRanges: next });
  }

  function toggleMultiSelectFilter(key: string, value: string) {
    const current = multiSelectFilters[key] ?? [];
    const nextValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const next = { ...multiSelectFilters, [key]: nextValues };

    if (nextValues.length === 0) {
      delete next[key];
    }

    setMultiSelectFilters(next);
    onFilterChange?.({ search, searchField, status: statusFilter, multiSelect: next, checkbox: checkboxFilters, dateRanges });
  }

  function toggleCheckboxFilter(key: string, checked: boolean) {
    const next = { ...checkboxFilters, [key]: checked };

    if (!checked) {
      delete next[key];
    }

    setCheckboxFilters(next);
    onFilterChange?.({ search, searchField, status: statusFilter, multiSelect: multiSelectFilters, checkbox: next, dateRanges });
  }

  // Client-side filter applied in addition to anything the server is
  // already doing. Safe overlap: when server pre-filtered, the client
  // filter matches everything and is a no-op. For pages backed by a
  // small local dataset (task detail, group detail, audit log, users,
  // admin users, admin companies) the client filter is the only one
  // actually running — which is why typing in "Search by" + picking a
  // status now works on those pages.
  const statusKey = filters?.statusKey ?? "status";
  const searchTerm = search.trim().toLowerCase();
  const statusSelected = statusFilter && filters?.statusOptions?.some((o) => o.value === statusFilter);

  const filteredData = serverSide ? data : data.filter((row) => {
    const rec = row as Record<string, unknown>;
    if (statusSelected) {
      const v = rec[statusKey];
      if (typeof v !== "string" || v.toLowerCase() !== statusFilter.toLowerCase()) {
        return false;
      }
    }
    if (searchTerm && searchField) {
      const v = rec[searchField];
      if (v == null || !String(v).toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    for (const [key, values] of Object.entries(multiSelectFilters)) {
      if (values.length === 0) continue;
      const v = rec[key];
      if (v == null || !values.includes(String(v))) {
        return false;
      }
    }
    for (const filter of dateRangeFilters) {
      if (!inDayRange(rec[filter.key], dateRanges[filter.key])) {
        return false;
      }
    }
    for (const filter of filters?.checkboxFilters ?? []) {
      if (!checkboxFilters[filter.key]) continue;
      const v = rec[filter.key];
      const mode = filter.mode ?? "truthy";

      if (mode === "missing" && v != null && String(v).trim() !== "") {
        return false;
      }
      if (mode === "truthy" && !v) {
        return false;
      }
      if (mode === "falsy" && v) {
        return false;
      }
    }
    return true;
  });

  // Local sort (if data is client-side)
  const sortedData = sortKey && !serverSide
    ? [...filteredData].sort((a, b) => {
        const av = String((a as Record<string, unknown>)[sortKey] ?? "");
        const bv = String((b as Record<string, unknown>)[sortKey] ?? "");
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : filteredData;

  return (
    <div>
      {/* Header row: title + actions */}
      {(title || actions) && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-baseline gap-2">
            {title && (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
                {totalCount != null && (
                  <span className="ml-1.5 text-sm font-normal text-gray-400 dark:text-riot-navy-400">({totalCount})</span>
                )}
              </h2>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}

      {/* Filter bar */}
      {filters && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {filters.searchFields && filters.searchFields.length > 0 && (
              <div className="flex overflow-hidden rounded-md border border-gray-300 dark:border-riot-navy-600">
                <select
                  value={searchField}
                  onChange={(e) => {
                    setSearchField(e.target.value);
                    if (search.trim()) emitFilters({ searchField: e.target.value });
                  }}
                  className="border-r border-gray-300 bg-riot-navy-50 px-2 py-1.5 text-xs text-gray-700 focus:outline-none dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200"
                  aria-label="Search field"
                >
                  {filters.searchFields.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder={filters.searchPlaceholder ?? "Search by"}
                  maxLength={filters.searchMaxLength}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); emitFilters({ search: e.target.value }); }}
                  className="w-36 bg-white px-2.5 py-1.5 text-xs placeholder:text-gray-400 focus:outline-none dark:bg-riot-navy-800 dark:text-riot-navy-200 sm:w-44"
                />
              </div>
            )}
            {filters.statusOptions && (
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); onFilterChange?.({ search, searchField, status: e.target.value, multiSelect: multiSelectFilters, checkbox: checkboxFilters, dateRanges }); }}
                className="rounded-md border border-gray-300 bg-riot-navy-50 px-2.5 py-1.5 text-xs text-gray-700 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200"
                aria-label={`Filter by ${filters.statusLabel ?? "status"}`}
              >
                <option value="">{filters.statusLabel ?? "Status"}</option>
                {filters.statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
            {filters.multiSelectFilters?.map((filter) => {
              const selected = multiSelectFilters[filter.key] ?? [];
              return (
                <div key={filter.key} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenMultiSelectFilter((current) => current === filter.key ? null : filter.key)}
                    className={`flex min-w-[150px] items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                      selected.length > 0 || openMultiSelectFilter === filter.key
                        ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                        : "border-gray-300 bg-riot-navy-50 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                    }`}
                    aria-expanded={openMultiSelectFilter === filter.key}
                    aria-label={`Filter by ${filter.label}`}
                  >
                    <span>{selected.length > 0 ? `${filter.label}: ${selected.length}` : filter.label}</span>
                    <ChevronDown size={13} className={openMultiSelectFilter === filter.key ? "rotate-180 transition-transform" : "transition-transform"} />
                  </button>
                  {openMultiSelectFilter === filter.key && (
                    <div className="absolute left-0 top-full z-20 mt-1 max-h-72 w-72 overflow-y-auto rounded-md border border-gray-200 bg-white py-2 shadow-lg dark:border-riot-navy-700 dark:bg-riot-navy-800">
                      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 pb-2 dark:border-riot-navy-700">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">{filter.label}</span>
                        {selected.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const next = { ...multiSelectFilters };
                              delete next[filter.key];
                              setMultiSelectFilters(next);
                              onFilterChange?.({ search, searchField, status: statusFilter, multiSelect: next, checkbox: checkboxFilters, dateRanges });
                            }}
                            className="text-[11px] font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="py-1">
                        {filter.options.map((option) => (
                          <label
                            key={option.value}
                            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                          >
                            <input
                              type="checkbox"
                              checked={selected.includes(option.value)}
                              onChange={() => toggleMultiSelectFilter(filter.key, option.value)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-riot-gold-500"
                            />
                            <span className="truncate">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filters.checkboxFilters?.map((filter) => (
              <label
                key={filter.key}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                  checkboxFilters[filter.key]
                    ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                    : "border-gray-300 bg-riot-navy-50 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checkboxFilters[filter.key] ?? false}
                  onChange={(e) => toggleCheckboxFilter(filter.key, e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-riot-gold-500"
                />
                {filter.label}
              </label>
            ))}
            {dateRangeFilters.length > 0 && (
              <button
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                aria-expanded={showMoreFilters}
                className={`flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                  showMoreFilters
                    ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                }`}
              >
                <Filter size={13} />
                More filters
              </button>
            )}

            <div className="flex-1" />

            {pagination && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-riot-navy-300">
                {pagination.pageSizeOptions && pagination.onPageSizeChange && (
                  <select
                    value={pagination.pageSize}
                    onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                    className="mr-1 rounded-md border border-gray-300 bg-riot-navy-50 px-1.5 py-1 text-xs text-gray-700 focus:outline-none dark:border-riot-navy-600 dark:bg-riot-navy-700 dark:text-riot-navy-200"
                    aria-label="Rows per page"
                  >
                    {pagination.pageSizeOptions.map((size) => (
                      <option key={size} value={size}>{size} / page</option>
                    ))}
                  </select>
                )}
                {pagination.rangeLabel && <span className="mr-1 whitespace-nowrap">{pagination.rangeLabel}</span>}
                <button onClick={pagination.onFirst} aria-label="First page" disabled={pagination.page <= 1} className="rounded p-1 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-riot-navy-700">
                  <ChevronsLeft size={14} />
                </button>
                <button onClick={pagination.onPrev} aria-label="Previous page" disabled={pagination.page <= 1} className="rounded p-1 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-riot-navy-700">
                  <ChevronLeft size={14} />
                </button>
                <span className="whitespace-nowrap px-1">
                  {pagination.totalPages != null
                    ? `${pagination.page} of ${Math.max(pagination.totalPages, pagination.page)}`
                    : `Page ${pagination.page}`}
                </span>
                <button onClick={pagination.onNext} aria-label="Next page" disabled={!pagination.hasMore} className="rounded p-1 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-riot-navy-700">
                  <ChevronRight size={14} />
                </button>
                <button disabled className="rounded p-1 hover:bg-gray-100 disabled:opacity-30">
                  <ChevronsRight size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Expanded More filters panel */}
          {showMoreFilters && dateRangeFilters.length > 0 && (
            <div className="mb-3 rounded-md border border-gray-200 bg-white p-3 dark:border-riot-navy-700 dark:bg-riot-navy-800">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {dateRangeFilters.flatMap((filter) =>
                  (["from", "to"] as const).map((end) => {
                    const id = `${dateInputId}-${filter.key}-${end}`;
                    return (
                      <div key={id}>
                        <label htmlFor={id} className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-riot-navy-300">
                          {filter.label} {end}
                        </label>
                        <input
                          id={id}
                          type="date"
                          value={dateRanges[filter.key]?.[end] ?? ""}
                          min={end === "to" ? dateRanges[filter.key]?.from : undefined}
                          max={end === "from" ? dateRanges[filter.key]?.to : undefined}
                          onChange={(e) => setDateRangeEnd(filter.key, end, e.target.value)}
                          className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-riot-navy-200"
                        />
                      </div>
                    );
                  }),
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => { setSearch(""); setStatusFilter(""); setMultiSelectFilters({}); setCheckboxFilters({}); setDateRanges({}); onFilterChange?.({ search: "", searchField, status: "", multiSelect: {}, checkbox: {}, dateRanges: {} }); }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                >
                  Clear filters
                </button>
                <button
                  onClick={() => setShowMoreFilters(false)}
                  className="rounded-md bg-riot-gold-400 px-3 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/80 dark:bg-riot-navy-900">
              {selectable && (
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-riot-gold-500"
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((col) => {
                const clientSortable = !!col.sortable && !serverSide;
                const serverSortable = serverSide && !!col.serverSortable && !!onServerSortChange;
                const activeServer = serverSortable && serverSort?.key === col.key ? serverSort : null;
                return (
                  <th
                    key={col.key}
                    // Width comes from caller-supplied data, so it can't
                    // be a static Tailwind class. Setting it via a ref
                    // callback (CSSOM mutation) keeps the rendered HTML
                    // free of inline `style="..."` so CSP can be
                    // `style-src 'self'` without `'unsafe-inline'`.
                    ref={(el) => {
                      if (el && col.width) el.style.width = col.width;
                    }}
                    className={`px-3 py-2.5 text-xs font-medium text-gray-500 ${clientSortable || serverSortable ? "cursor-pointer select-none" : ""}`}
                    aria-sort={
                      activeServer
                        ? activeServer.direction === "asc" ? "ascending" : "descending"
                        : clientSortable && sortKey === col.key
                          ? sortDir === "asc" ? "ascending" : "descending"
                          : undefined
                    }
                    onClick={
                      clientSortable
                        ? () => handleSort(col.key)
                        : serverSortable
                          ? () => onServerSortChange?.(nextServerSort(serverSort, col.key, col.initialSortDirection))
                          : undefined
                    }
                    tabIndex={clientSortable || serverSortable ? 0 : undefined}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      if (!clientSortable && !serverSortable) return;
                      event.preventDefault();
                      if (clientSortable) handleSort(col.key);
                      else onServerSortChange?.(nextServerSort(serverSort, col.key, col.initialSortDirection));
                    }}
                    title={serverSortable ? "Click to sort; click again to reverse or restore default order" : clientSortable ? "Click to sort; click again to reverse" : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.header}
                      {clientSortable && (
                        <span className="text-gray-300">
                          {sortKey === col.key ? (
                            sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                          ) : (
                            <ChevronsUpDown size={13} />
                          )}
                        </span>
                      )}
                      {serverSortable && (
                        <span className="text-gray-300">
                          {activeServer ? (
                            activeServer.direction === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                          ) : (
                            <ChevronsUpDown size={13} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                  );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-riot-navy-700">
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-3 py-10 text-center text-sm text-gray-400 dark:text-riot-navy-400">
                  {emptyMessage ?? "No data found"}
                </td>
              </tr>
            ) : (
              sortedData.map((row) => {
                const key = keyExtractor(row);
                return (
                  <tr
                    key={key}
                    onClick={() => onRowClick?.(row)}
                    className={`transition-colors ${onRowClick ? "cursor-pointer" : ""} ${
                      selectedRows.has(key) ? "bg-riot-gold-50/50" : "hover:bg-gray-50/50"
                    }`}
                  >
                    {selectable && (
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(key)}
                          onChange={() => toggleRow(key)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-riot-gold-500"
                          aria-label={`Select row ${key}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2.5 text-gray-700 dark:text-riot-navy-200">
                        {col.render
                          ? col.render(row)
                          : String((row as Record<string, unknown>)[col.key] ?? "-")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
